import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAdminUsuarios } from '../hooks/useAdminUsuarios';

interface Props {
  open: boolean;
  usuarioId: string;
  usuarioNome: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AlterarSenhaDialog({ open, usuarioId, usuarioNome, onClose, onSuccess }: Props) {
  const { changePasswordMutation } = useAdminUsuarios();
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [showNova, setShowNova] = useState(false);
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const senhaValida = novaSenha.length >= 8;
  const senhasCoincidem = novaSenha === confirmar;
  const podeSalvar = senhaValida && senhasCoincidem && !changePasswordMutation.isPending;

  const handleClose = () => {
    setNovaSenha('');
    setConfirmar('');
    setErro(null);
    onClose();
  };

  const handleSalvar = async () => {
    setErro(null);
    try {
      await changePasswordMutation.mutateAsync({ id: usuarioId, nova_senha: novaSenha });
      setNovaSenha('');
      setConfirmar('');
      onSuccess();
    } catch (err: any) {
      setErro(err?.response?.data?.error ?? 'Erro ao alterar senha. Tente novamente.');
    }
  };

  const senhaHint =
    novaSenha.length > 0 && !senhaValida
      ? 'Mínimo 8 caracteres'
      : confirmar.length > 0 && !senhasCoincidem
        ? 'As senhas não coincidem'
        : undefined;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Alterar senha — {usuarioNome}</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {erro && <Alert severity="error">{erro}</Alert>}
        <TextField
          label="Nova senha"
          type={showNova ? 'text' : 'password'}
          value={novaSenha}
          onChange={(e) => setNovaSenha(e.target.value)}
          fullWidth
          error={novaSenha.length > 0 && !senhaValida}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNova((v) => !v)} edge="end">
                  {showNova ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="Confirmar senha"
          type={showConfirmar ? 'text' : 'password'}
          value={confirmar}
          onChange={(e) => setConfirmar(e.target.value)}
          fullWidth
          error={confirmar.length > 0 && !senhasCoincidem}
          helperText={senhaHint}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmar((v) => !v)} edge="end">
                  {showConfirmar ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={changePasswordMutation.isPending}>
          Cancelar
        </Button>
        <Button onClick={handleSalvar} variant="contained" disabled={!podeSalvar}>
          {changePasswordMutation.isPending ? <CircularProgress size={20} /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
